import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface HardinessZone {
  id: string;
  zone_code: string;
  zone_name: string;
  zone_system: string;
  min_temp_f: number | null;
  max_temp_f: number | null;
  min_temp_c: number | null;
  max_temp_c: number | null;
  description: string | null;
  regions: string[] | null;
}

export const useHardinessZones = (system?: string) => {
  const [zones, setZones] = useState<HardinessZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoading(true);
        let query = supabase.from('hardiness_zones').select('*');
        
        if (system) {
          query = query.eq('zone_system', system);
        }
        
        const { data, error } = await query.order('zone_system').order('zone_code');

        if (error) {
          console.error('Error fetching hardiness zones:', error);
          setError(error.message);
          return;
        }

        setZones(data || []);
      } catch (err) {
        console.error('Error fetching zones:', err);
        setError('Failed to fetch hardiness zones');
      } finally {
        setLoading(false);
      }
    };

    fetchZones();
  }, [system]);

  // Group zones by system for easier display
  const zonesBySystem = zones.reduce((acc, zone) => {
    if (!acc[zone.zone_system]) {
      acc[zone.zone_system] = [];
    }
    acc[zone.zone_system].push(zone);
    return acc;
  }, {} as Record<string, HardinessZone[]>);

  return {
    zones,
    zonesBySystem,
    loading,
    error,
  };
};